import { Instagram, Youtube, MapPin, Globe, Mail, ArrowUp } from "lucide-react";
import { motion } from "framer-motion";

const socialLinks = [
  {
    name: "Instagram",
    icon: Instagram,
    href: "https://www.instagram.com/global_maths_tutoring_network",
  },
  {
    name: "YouTube",
    icon: Youtube,
    href: "https://youtube.com/@globalmathtutoringnetwork",
  },
  {
    name: "Website",
    icon: Globe,
    href: "https://global-math-tutoring-network.netlify.app/",
  },
];

const quickLinks = [
  { label: "Home", href: "#hero" },
  { label: "Tutors", href: "#our-tutors" },
  { label: "Courses", href: "#services" },
  { label: "About Us", href: "#about-us" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact-us" },
];

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative bg-slate-900 text-white overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-gradient-to-br from-blue-600/10 to-violet-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-gradient-to-tr from-amber-600/10 to-orange-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="relative w-10 h-10 flex-shrink-0">
                {/* Square Border */}
                <div className="absolute inset-0 border-2 border-blue-500 rounded-lg shadow-lg shadow-blue-500/20"></div>
                
                {/* Rotating Logo Inside Square Border */}
                <motion.div
                  className="absolute inset-1 overflow-hidden rounded"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  whileHover={{ scale: 1.05 }}
                >
                  <img 
                    src="/globalmath.svg" 
                    alt="Global Math Tutoring Network"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Global Math Tutoring Network</h2>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Based in <strong className="text-white">Pune, Maharashtra, India</strong> — delivering trusted online Math & Coding tutoring to students <strong className="text-white">worldwide</strong>.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 hover:scale-110"
                  aria-label={link.name}
                >
                  <link.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:ml-8"
          >
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => {
                      const element = document.getElementById(link.href.replace('#', ''));
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-2 group text-left bg-transparent border-none cursor-pointer"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-500 group-hover:bg-blue-500 transition-colors" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <div className="space-y-3 text-sm">
              <p className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Pune, Maharashtra, India</span>
              </p>
              <a
                href="mailto:globalmathtutoringnetwork@gmail.com"
                className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors"
              >
                <Mail className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>globalmathtutoringnetwork@gmail.com</span>
              </a>
            </div>
          </motion.div>

          {/* Newsletter / CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="text-white font-semibold mb-4">Ready to Start?</h3>
            <p className="text-slate-400 text-sm mb-4">
              Join thousands of students achieving academic excellence with our expert tutors.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact-us');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-violet-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
            >
              Get Started
              <ArrowUp className="w-4 h-4" />
            </button>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 py-6 flex flex-col items-center justify-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Global Math Tutoring Network
          </p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg transition-all duration-300"
          >
            Back to Top
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
}
